import { useCallback, useEffect, useMemo, useState } from "react";
import { Calculator, Loader2, RefreshCw, Sparkles, TrendingUp, AlertTriangle, CheckCircle2, Info } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from "recharts";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { aiFundingCalculator } from "@/lib/ai";

const CURRENCIES = [
  { code: "INR", symbol: "₹" },
  { code: "USD", symbol: "$" },
  { code: "EUR", symbol: "€" },
  { code: "GBP", symbol: "£" },
];

const DEFAULTS = {
  product_name: "",
  manufacturing_cost_per_unit: 800,
  moq: 500,
  packaging_cost: 50,
  shipping_cost_per_unit: 120,
  import_duties_pct: 15,
  platform_fee_pct: 5,
  payment_processing_pct: 3,
  marketing_budget: 100000,
  taxes_pct: 0,
  contingency_pct: 10,
  expected_backers: 500,
  reward_price: 2499,
  country: "IN",
  currency: "INR",
};

const NumberField = ({ label, value, onChange, min = 0, max, step = 1, suffix, testid }) => (
  <div>
    <label className="text-xs font-semibold text-navy-700 uppercase tracking-wide">{label}</label>
    <div className="mt-1.5 relative">
      <input
        type="number"
        value={value}
        min={min} max={max} step={step}
        onChange={(e) => onChange(Number(e.target.value))}
        data-testid={testid}
        className="w-full h-11 rounded-2xl border border-warm-200 bg-white px-3 pr-10 text-sm text-navy-800 focus:outline-none focus:border-saffron-500 focus:ring-4 focus:ring-saffron-500/15 transition"
      />
      {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-navy-400">{suffix}</span>}
    </div>
  </div>
);

const Slider = ({ label, value, onChange, min = 0, max = 100, step = 1, suffix = "%", testid }) => (
  <div>
    <div className="flex justify-between items-baseline">
      <label className="text-xs font-semibold text-navy-700 uppercase tracking-wide">{label}</label>
      <span className="font-display text-sm font-bold text-saffron-700">{value}{suffix}</span>
    </div>
    <input
      type="range"
      min={min} max={max} step={step}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      data-testid={testid}
      className="mt-2 w-full h-2 bg-warm-100 rounded-full appearance-none cursor-pointer accent-saffron-600"
    />
  </div>
);

const money = (v, cur) => {
  const c = CURRENCIES.find((x) => x.code === cur) || CURRENCIES[0];
  return `${c.symbol}${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
};

const StatCard = ({ label, value, sub, tone = "default", testid }) => {
  const toneCls = {
    default: "bg-white border-warm-200",
    good:    "bg-forest-500/10 border-forest-500/30",
    warn:    "bg-saffron-500/10 border-saffron-500/30",
    hero:    "bg-navy-800 border-navy-800 text-white",
  }[tone];
  const labelCls = tone === "hero" ? "text-navy-200" : "text-navy-500";
  const valCls   = tone === "hero" ? "text-white" : "text-navy-800";
  return (
    <div className={`rounded-2xl border p-5 ${toneCls}`} data-testid={testid}>
      <div className={`text-[11px] font-semibold uppercase tracking-[0.16em] ${labelCls}`}>{label}</div>
      <div className={`font-display font-bold text-3xl mt-1 tracking-tight ${valCls}`}>{value}</div>
      {sub && <div className={`text-xs mt-1 ${labelCls}`}>{sub}</div>}
    </div>
  );
};

const RECS_STYLE = {
  positive: { icon: CheckCircle2, cls: "bg-forest-500/10 text-forest-700 border-forest-500/30" },
  warning:  { icon: AlertTriangle, cls: "bg-saffron-500/10 text-saffron-700 border-saffron-500/30" },
  critical: { icon: AlertTriangle, cls: "bg-red-100 text-red-700 border-red-300" },
};

const PIE_COLORS = ["#E85D04", "#FAA307", "#2D6A4F", "#0D1B2A", "#B5C1D0", "#F4EFE6"];

const FundingCalculator = () => {
  const { user, loading: authLoading, requireAuth } = useAuth();
  const [inputs, setInputs] = useState(DEFAULTS);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) requireAuth("Sign in to use the AI Funding Calculator.");
  }, [user, authLoading, requireAuth]);

  const set = (k, v) => setInputs((prev) => ({ ...prev, [k]: v }));

  const calculate = useCallback(async () => {
    setLoading(true);
    try {
      const r = await aiFundingCalculator(inputs);
      setResult(r.data);
    } catch (e) {
      toast.error(e.message || "Calculation failed");
    } finally { setLoading(false); }
  }, [inputs]);

  // Auto-calc on first mount when a user is present
  useEffect(() => {
    if (user) calculate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const budgetPie = useMemo(() => {
    if (!result?.numbers) return [];
    const n = result.numbers;
    return [
      { name: "Manufacturing + landing", value: n.cogs_total },
      { name: "Marketing", value: n.marketing_budget },
      { name: "Platform + payment fees", value: n.platform_fees + n.payment_fees },
      { name: "Taxes", value: n.taxes },
      { name: "Contingency", value: n.contingency },
      { name: "Emergency buffer", value: n.emergency_buffer },
    ].filter((x) => x.value > 0);
  }, [result]);

  const marginBar = useMemo(() => {
    if (!result?.numbers) return [];
    const n = result.numbers;
    return [
      { name: "Gross revenue", value: n.gross_revenue },
      { name: "Total costs",   value: Math.max(0, n.gross_revenue - n.expected_profit) },
      { name: "Profit",        value: Math.max(0, n.expected_profit) },
    ];
  }, [result]);

  const cur = inputs.currency;

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10" data-testid="funding-calculator-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-saffron-700">
            <Calculator className="h-4 w-4" />
            <span className="text-xs font-semibold uppercase tracking-[0.2em]">AI Funding Calculator</span>
          </div>
          <h1 className="mt-1 font-display text-3xl md:text-4xl font-bold tracking-tight text-navy-800">Model your campaign in real numbers.</h1>
          <p className="mt-2 text-navy-600 max-w-2xl">Sliders adjust instantly. Tap <span className="font-semibold text-saffron-700">Recalculate</span> to refresh the AI critique.</p>
        </div>
        <button
          onClick={calculate}
          disabled={loading}
          data-testid="funding-calc-recalculate"
          className="btn-saffron inline-flex items-center gap-2 rounded-2xl h-11 px-5 font-semibold disabled:opacity-70"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          {loading ? "Crunching…" : "Recalculate"}
        </button>
      </div>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* INPUTS */}
        <div className="lg:col-span-5 rounded-2xl border border-warm-200 bg-white p-6 space-y-6">
          <div>
            <label className="text-xs font-semibold text-navy-700 uppercase tracking-wide">Product name (optional)</label>
            <input
              value={inputs.product_name}
              onChange={(e) => set("product_name", e.target.value)}
              placeholder="e.g. AquaSense"
              className="mt-1.5 w-full h-11 rounded-2xl border border-warm-200 bg-white px-3 text-sm focus:outline-none focus:border-saffron-500 focus:ring-4 focus:ring-saffron-500/15 transition"
              data-testid="fc-product-name"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <NumberField label="Manufacturing / unit" value={inputs.manufacturing_cost_per_unit} onChange={(v) => set("manufacturing_cost_per_unit", v)} suffix={cur} testid="fc-mfg" />
            <NumberField label="MOQ" value={inputs.moq} onChange={(v) => set("moq", v)} testid="fc-moq" />
            <NumberField label="Packaging / unit" value={inputs.packaging_cost} onChange={(v) => set("packaging_cost", v)} suffix={cur} testid="fc-packaging" />
            <NumberField label="Shipping / unit" value={inputs.shipping_cost_per_unit} onChange={(v) => set("shipping_cost_per_unit", v)} suffix={cur} testid="fc-shipping" />
            <NumberField label="Expected backers" value={inputs.expected_backers} onChange={(v) => set("expected_backers", v)} testid="fc-backers" />
            <NumberField label="Reward price" value={inputs.reward_price} onChange={(v) => set("reward_price", v)} suffix={cur} testid="fc-price" />
            <NumberField label="Marketing budget" value={inputs.marketing_budget} onChange={(v) => set("marketing_budget", v)} suffix={cur} testid="fc-marketing" />
            <div>
              <label className="text-xs font-semibold text-navy-700 uppercase tracking-wide">Currency</label>
              <select
                value={inputs.currency}
                onChange={(e) => set("currency", e.target.value)}
                className="mt-1.5 w-full h-11 rounded-2xl border border-warm-200 bg-white px-3 text-sm focus:outline-none focus:border-saffron-500 focus:ring-4 focus:ring-saffron-500/15 transition"
                data-testid="fc-currency"
              >
                {CURRENCIES.map((c) => <option key={c.code} value={c.code}>{c.code} ({c.symbol})</option>)}
              </select>
            </div>
          </div>

          <div className="space-y-4 pt-2 border-t border-warm-100">
            <Slider label="Import duties" value={inputs.import_duties_pct} onChange={(v) => set("import_duties_pct", v)} max={40} testid="fc-duties-slider" />
            <Slider label="Contingency" value={inputs.contingency_pct} onChange={(v) => set("contingency_pct", v)} max={30} testid="fc-contingency-slider" />
            <Slider label="Taxes" value={inputs.taxes_pct} onChange={(v) => set("taxes_pct", v)} max={30} testid="fc-taxes-slider" />
            <Slider label="Platform fee" value={inputs.platform_fee_pct} onChange={(v) => set("platform_fee_pct", v)} max={15} testid="fc-platform-slider" />
            <Slider label="Payment processing" value={inputs.payment_processing_pct} onChange={(v) => set("payment_processing_pct", v)} max={10} step={0.5} testid="fc-payment-slider" />
          </div>
        </div>

        {/* RESULTS */}
        <div className="lg:col-span-7 space-y-4">
          {!result && loading && (
            <div className="rounded-2xl border border-warm-200 bg-white p-16 text-center">
              <Loader2 className="h-8 w-8 mx-auto animate-spin text-saffron-600" />
              <p className="mt-3 text-navy-500">Modeling your economics…</p>
            </div>
          )}
          {result && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <StatCard
                  label="Recommended goal"
                  value={money(result.numbers.recommended_funding_goal, cur)}
                  sub={`for ${inputs.expected_backers} backers`}
                  tone="hero"
                  testid="fc-stat-goal"
                />
                <StatCard
                  label="Expected profit"
                  value={money(result.numbers.expected_profit, cur)}
                  sub={`${result.numbers.profit_margin_pct}% margin`}
                  tone={result.numbers.profit_margin_pct >= 20 ? "good" : "warn"}
                  testid="fc-stat-profit"
                />
                <StatCard
                  label="Break-even at"
                  value={result.numbers.break_even_backers ? `${result.numbers.break_even_backers}` : "N/A"}
                  sub="backers"
                  testid="fc-stat-breakeven"
                />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="rounded-2xl border border-warm-200 bg-white p-5">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-display font-bold text-navy-800">Budget allocation</h3>
                    <span className="text-xs text-navy-500">Where the goal goes</span>
                  </div>
                  <div className="h-56">
                    <ResponsiveContainer>
                      <PieChart>
                        <Pie data={budgetPie} dataKey="value" cx="50%" cy="50%" innerRadius={45} outerRadius={80} paddingAngle={2}>
                          {budgetPie.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                        </Pie>
                        <Tooltip formatter={(v) => money(v, cur)} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="mt-2 grid grid-cols-2 gap-1.5">
                    {budgetPie.map((item, i) => (
                      <div key={item.name} className="flex items-center gap-1.5 text-[11px] text-navy-600">
                        <span className="h-2 w-2 rounded-sm flex-shrink-0" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                        <span className="truncate">{item.name}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="rounded-2xl border border-warm-200 bg-white p-5">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-display font-bold text-navy-800">Revenue vs costs vs profit</h3>
                    <span className="text-xs text-navy-500">At {inputs.expected_backers} backers</span>
                  </div>
                  <div className="h-56">
                    <ResponsiveContainer>
                      <BarChart data={marginBar}>
                        <CartesianGrid stroke="#F4EFE6" vertical={false} />
                        <XAxis dataKey="name" tick={{ fill: "#4A6180", fontSize: 11 }} tickLine={false} axisLine={false} />
                        <YAxis hide />
                        <Tooltip formatter={(v) => money(v, cur)} />
                        <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                          <Cell fill="#0D1B2A" />
                          <Cell fill="#B5C1D0" />
                          <Cell fill="#E85D04" />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>

              {result.ai && (
                <div className="rounded-2xl border border-warm-200 bg-white p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <Sparkles className="h-4 w-4 text-saffron-600" />
                    <h3 className="font-display font-bold text-navy-800">AI recommendations</h3>
                    {result.ai.analysis?.summary && <span className="text-xs text-navy-500 hidden md:inline">· {result.ai.analysis.summary}</span>}
                  </div>
                  <div className="space-y-2">
                    {(result.ai.recommendations || []).map((r, i) => {
                      const s = RECS_STYLE[r.level] || RECS_STYLE.warning;
                      const Icon = s.icon;
                      return (
                        <div key={i} data-testid={`fc-rec-${i}`} className={`flex items-start gap-2.5 rounded-xl border px-3 py-2.5 text-sm ${s.cls}`}>
                          <Icon className="h-4 w-4 flex-shrink-0 mt-0.5" />
                          <span>{r.text}</span>
                        </div>
                      );
                    })}
                  </div>
                  {(result.ai.benchmarks || []).length > 0 && (
                    <div className="mt-4 pt-4 border-t border-warm-100">
                      <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-navy-500 mb-2 flex items-center gap-1">
                        <TrendingUp className="h-3 w-3" /> Benchmarks
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {result.ai.benchmarks.map((b, i) => (
                          <div key={i} className="rounded-xl bg-cream-100 px-3 py-2 text-xs">
                            <div className="font-semibold text-navy-800">{b.metric}</div>
                            <div className="text-navy-600 mt-0.5">You: <span className="font-semibold">{String(b.user_value)}</span> · Industry: <span className="font-semibold">{String(b.industry)}</span></div>
                            <div className="text-[10px] uppercase font-semibold tracking-wider text-saffron-700 mt-1">{b.verdict}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="rounded-2xl border border-warm-200 bg-cream-100 p-4 text-xs text-navy-600 flex items-start gap-2">
                <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0 text-saffron-600" />
                <span>Numbers are computed from your inputs. AI advice is directional — validate with a local accountant before publishing.</span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default FundingCalculator;
